// The firm's one logo, as its staff see it and set it for every integration.
// `orgId` is the firm the card was opened for (null: the staff member's own),
// named by every request, so a save lands in that firm whatever is chosen
// while it runs.

import { Show, createResource, createSignal, onCleanup } from "solid-js";

import AsyncResource from "./AsyncResource";
import { api, failureSentence } from "../lib/api";
import { logoFileBase64 } from "../lib/operatorInputs";

export default function FirmLogoCard(props: { orgId: string | null; canEdit: boolean }) {
  const [shown, { mutate }] = createResource(() => api.brandLogo(props.orgId));
  // The chosen file shows from a local address until it is saved; the stored
  // logo shows from the trading site, as traders get it.
  const [logoFile, setLogoFile] = createSignal<File | null>(null);
  const [logoPreview, setLogoPreview] = createSignal<string | null>(null);
  const [savingLogo, setSavingLogo] = createSignal(false);
  const [logoError, setLogoError] = createSignal<string | null>(null);
  const [logoSaved, setLogoSaved] = createSignal(false);

  function chooseLogo(file: File | null) {
    const previous = logoPreview();
    if (previous) URL.revokeObjectURL(previous);
    setLogoFile(file);
    setLogoPreview(file ? URL.createObjectURL(file) : null);
    setLogoError(null);
    setLogoSaved(false);
  }
  onCleanup(() => chooseLogo(null));

  /** Wear `file` as the logo, or clear the logo with null. */
  async function onSaveLogo(file: File | null) {
    setSavingLogo(true);
    setLogoError(null);
    setLogoSaved(false);
    try {
      const setting = await api.setBrandLogo(
        props.orgId,
        file
          ? {
              change: "set",
              image_base64: logoFileBase64(new Uint8Array(await file.arrayBuffer())),
            }
          : { change: "clear" },
      );
      mutate(setting);
      chooseLogo(null);
      setLogoSaved(true);
    } catch (e) {
      setLogoError(failureSentence(e));
    } finally {
      setSavingLogo(false);
    }
  }

  return (
    <div class="card mb-4">
      <div class="card-header">
        <h2 class="text-sm font-semibold">Firm logo</h2>
      </div>
      <div class="card-body space-y-3">
        <AsyncResource resource={shown}>
          {(current) => (
            <>
              <Show when={current().logo} fallback="—">
                {(logo) => (
                  <img class="h-10 max-w-40 object-contain" src={logo().url} alt="" />
                )}
              </Show>
              <p class="text-sm text-slate-600">
                What your traders see on your launch pages and in the terminal: a
                PNG, WebP or JPEG of at most 256 KB and 1024 pixels on a side.
              </p>
              <Show when={props.canEdit}>
                <input
                  id="brand-logo"
                  type="file"
                  accept="image/png,image/webp,image/jpeg"
                  onChange={(e) => chooseLogo(e.currentTarget.files?.[0] ?? null)}
                />
                <Show when={logoPreview()}>
                  {(preview) => (
                    <div class="flex items-center gap-3">
                      <img class="h-10 max-w-40 object-contain" src={preview()} alt="" />
                      <span class="text-xs text-slate-500">Not saved yet</span>
                    </div>
                  )}
                </Show>
                <Show when={logoError()}>
                  <p class="text-sm text-red-600">{logoError()}</p>
                </Show>
                <Show when={logoSaved()}>
                  <p class="text-sm text-emerald-700">Saved.</p>
                </Show>
                <div class="flex flex-wrap gap-2">
                  <button
                    type="button"
                    class="btn-primary"
                    disabled={!logoFile() || savingLogo()}
                    onClick={() => void onSaveLogo(logoFile())}
                  >
                    {savingLogo() ? "Saving…" : "Save logo"}
                  </button>
                  <Show when={current().logo}>
                    <button
                      type="button"
                      class="btn-secondary"
                      disabled={savingLogo()}
                      onClick={() => void onSaveLogo(null)}
                    >
                      Remove logo
                    </button>
                  </Show>
                </div>
              </Show>
            </>
          )}
        </AsyncResource>
      </div>
    </div>
  );
}
